import { DashboardApiFilters, fetchDashboardEndpoint, requestDashboardExport } from "../dashboardApi";

export type AnalyticsPeriod = "by_week" | "by_month" | "by_year";

export interface RevenuePoint {
  date: string;
  revenue: number;
  sales: number;
}

export interface RevenueAnalytics {
  total_revenue: number;
  total_sales: number;
  by_week: RevenuePoint[];
  by_month: RevenuePoint[];
  by_year: RevenuePoint[];
}

export interface TrafficSource {
  source: string;
  count: number;
  amount: number;
}

export interface LatestContact {
  contact_id: number;
  full_name: string;
  avatar_url: string | null;
}

export interface Scorecard {
  key: string;
  display_name: string;
  amount: number | null;
  count: number | null;
  growth: string;
  latest_contacts: LatestContact[];
}

export interface TopDeal {
  deal_id: number;
  deal_name: string;
  deal_amount: number;
  company_name: string;
  country: string;
  company_logo_url: string | null;
}

export type DealOutcome = "won" | "lost" | "open";

export interface DealOutcomeStat {
  amount: number;
  count: number;
}

export interface DealCountStats {
  total_deals: number;
  won_deals: number;
  open_deals: number;
  lost_deals: number;
  pipelines: number;
  growth: string;
}

export interface RecentDeal {
  deal_id: number;
  deal_name: string;
  stage_name: string;
  deal_value: number;
  tags: string[];
  owner_name: string;
  owner_avatar_url: string | null;
  status: string;
}

export interface DashboardStats {
  revenueAnalytics: RevenueAnalytics;
  trafficSources: TrafficSource[];
  scorecards: Scorecard[];
  topDeals: TopDeal[];
  dealStatsByAmount: Record<DealOutcome, DealOutcomeStat>;
  dealStatsByCount: DealCountStats;
  recentDeals: RecentDeal[];
}

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toOptionalNumber = (value: unknown) =>
  value === undefined || value === null || value === "" ? null : toNumber(value);

const toText = (value: unknown) => (value === null || value === undefined ? "" : String(value));

const growthOf = (raw: any) => {
  if (!raw || typeof raw !== "object") return "";
  const key = Object.keys(raw).find(
    (field) => /(percentage|growth_rate)_(from|by)_last_(period|week)$/.test(field) || field === "percentage_from_last_week"
  );
  return key ? toText(raw[key]) : "";
};

const normalizeRevenuePoints = (raw: unknown): RevenuePoint[] =>
  asArray<any>(raw).map((point) => ({
    date: toText(point?.date),
    revenue: toNumber(point?.revenue),
    sales: toNumber(point?.sales),
  }));

const normalizeTopDeals = (raw: unknown): TopDeal[] => {
  const list = Array.isArray(raw) ? raw : asArray<any>((raw as any)?.last_30_days);
  return list.map((deal: any) => ({
    deal_id: toNumber(deal?.deal_id),
    deal_name: toText(deal?.deal_name),
    deal_amount: toNumber(deal?.deal_amount),
    company_name: toText(deal?.company_name),
    country: toText(deal?.country),
    company_logo_url: deal?.company_logo_url || null,
  }));
};

const normalizeOutcome = (raw: any): DealOutcomeStat => ({
  amount: toNumber(raw?.amount ?? raw?.revenue),
  count: toNumber(raw?.count),
});

export const normalizeDashboardStats = (raw: any): DashboardStats => {
  const revenue = raw?.revenue_analytics ?? {};
  const byAmount = raw?.deal_statistics_by_amount ?? {};
  const byCount = raw?.deals_statistics_by_count ?? {};

  return {
    revenueAnalytics: {
      total_revenue: toNumber(revenue.total_revenue),
      total_sales: toNumber(revenue.total_sales),
      by_week: normalizeRevenuePoints(revenue.by_week),
      by_month: normalizeRevenuePoints(revenue.by_month),
      by_year: normalizeRevenuePoints(revenue.by_year),
    },
    trafficSources: asArray<any>(raw?.deal_by_traffic_sources ?? raw?.traffic_sources).map((source) => ({
      source: toText(source?.source ?? source?.source_name),
      count: toNumber(source?.count),
      amount: toNumber(source?.amount ?? source?.revenue),
    })),
    scorecards: asArray<any>(raw?.scorecards).map((card) => ({
      key: toText(card?.key),
      display_name: toText(card?.display_name ?? card?.key),
      amount: toOptionalNumber(card?.amount ?? card?.value),
      count: toOptionalNumber(card?.count),
      growth: growthOf(card),
      latest_contacts: asArray<any>(card?.latest_contacts).map((contact) => ({
        contact_id: toNumber(contact?.contact_id),
        full_name: toText(contact?.full_name),
        avatar_url: contact?.avatar_url || null,
      })),
    })),
    topDeals: normalizeTopDeals(raw?.top_deals),
    dealStatsByAmount: {
      won: normalizeOutcome(byAmount.won),
      lost: normalizeOutcome(byAmount.lost),
      open: normalizeOutcome(byAmount.open),
    },
    dealStatsByCount: {
      total_deals: toNumber(byCount.total_deals),
      won_deals: toNumber(byCount.won_deals),
      open_deals: toNumber(byCount.open_deals),
      lost_deals: toNumber(byCount.lost_deals),
      pipelines: toNumber(byCount.pipelines),
      growth: growthOf(byCount),
    },
    recentDeals: asArray<any>(raw?.recent_deals).map((deal) => ({
      deal_id: toNumber(deal?.deal_id),
      deal_name: toText(deal?.deal_name),
      stage_name: toText(deal?.stage_name),
      deal_value: toNumber(deal?.deal_value),
      tags: asArray<unknown>(deal?.tags).map(toText).filter(Boolean),
      owner_name: toText(deal?.owner?.owner_name ?? deal?.owner?.name),
      owner_avatar_url: deal?.owner?.owner_avatar_url ?? deal?.owner?.avatar_url ?? null,
      status: toText(deal?.status),
    })),
  };
};

export const fetchDashboardStats = async (
  tenantId: string | number,
  filters: DashboardApiFilters = {}
): Promise<DashboardStats> =>
  normalizeDashboardStats(
    await fetchDashboardEndpoint("/dashboard/complete-stats/", tenantId, filters, "dashboard stats")
  );

export const exportDashboardStatsExcel = (tenantId: string | number, filters: DashboardApiFilters = {}) =>
  requestDashboardExport("/dashboard/complete-stats/export/", tenantId, filters, "dashboard stats");
